import React, {useEffect} from 'react'
import {getLogger} from '../../services/logging'
import IbeProgress from '../../assets/ibe-progress.png'
import BookingFailed from '../../assets/ibe-booking-failed.png'
import {FocusTrap} from 'focus-trap-react'
import {usePaymentStatusPoller} from '../../hooks/usePaymentStatusPoller'
import {useBookingConfirmationPoller} from '../../hooks/useBookingConfirmationPoller'
import {useAppDispatch, useAppSelector} from '../../store/hooks'
import {
  clearBooking,
  selectBookingStatus,
  setBookingId,
  setBookingStatus
} from '../../store/slices/bookingSlice'
import {setAppView} from '../../store/slices/appViewSlice'
import {AppView, BookingStatusValues, PaymentStatusValues} from '../../schema'
import {
  clearPayment,
  selectPaymentError,
  selectPaymentStatus,
  setPaymentError,
  setPaymentStatus
} from '../../store/slices/paymentSlice'
import {pushDataLayerEvent} from '../../services/dataLayer'
import useCartValidator from '../../hooks/useCartValidator'
import {isSameDay} from '../../services/dateUtils'
import {
  selectCartItems,
  selectPropertyId
} from '../../store/slices/shoppingCartSlice'
import {selectPropertyById} from '../../store/slices/configSlice'
import {calcCartPriceSummary} from '../../services/offerUtils'

const logger = getLogger('CheckoutProcessingModal')

export interface CheckoutProcessingModalProps {
  isOpen: boolean
  paymentSessionId?: string | null
}

/**
 * Formats the error message for display.
 * @param error The raw Adyen error message
 * @returns
 */
function formatErrorMessage(error?: string): string {
  const defaultMessage =
    'The payment failed for an unknown reason.  ' +
    'Please double check your card details or try again with a different card.'
  if (!error) {
    return defaultMessage
  }

  const normalizedError = error.toLowerCase()
  switch (normalizedError) {
    case 'blocked card':
    case 'fraud':
    case 'fraud-cancelled':
    case 'issuer suspected fraud':
    case 'refused':
      return (
        'The payment failed because the transaction was refused by the card issuer.  ' +
        'Please check with your card issuer or try a different card.'
      )
    case 'not enough balance':
      return (
        'The payment failed due to insufficient funds.  ' +
        'Please check with your card issuer or try a different card.'
      )
    case 'invalid card number':
      return (
        'The payment failed due to an invalid card number.  ' +
        'Please double check your card details and try again.'
      )
    default:
      logger.warn({
        message: `Unknown payment error occurred: ${error}`,
        data: {error, normalizedError}
      })
      return defaultMessage
  }
}

const CheckoutProcessingModal = ({
  isOpen,
  paymentSessionId
}: CheckoutProcessingModalProps) => {
  const dispatch = useAppDispatch()
  const paymentStatus = useAppSelector(selectPaymentStatus)
  const paymentError = useAppSelector(selectPaymentError)
  const bookingStatus = useAppSelector(selectBookingStatus)
  const cartItems = useAppSelector(selectCartItems)
  const today = new Date()
  const hasSameDayBooking = cartItems.some(cartItem => {
    const checkInDate = cartItem.offer?.arrival
    return checkInDate && isSameDay(checkInDate, today)
  })
  const propertyId = useAppSelector(selectPropertyId)
  const propertyConfig = useAppSelector(selectPropertyById(propertyId))
  const propertyPhone = propertyConfig?.phone

  const {pollForPaymentStatus} = usePaymentStatusPoller()
  const {pollForBookingConfirmation} = useBookingConfirmationPoller()
  const {validate} = useCartValidator()

  // poll for payment status
  useEffect(() => {
    if (paymentSessionId) {
      logger.info({
        message:
          'Payment session ID provided, start polling for payment status',
        data: {paymentSessionId}
      })

      pollForPaymentStatus({
        paymentSessionId,
        onPaymentSuccess: () => {
          logger.info({
            message: 'Payment succeeded, stop polling for status'
          })
          pushDataLayerEvent({
            event: 'Checkout_Payment_Success',
            category: 'IBE',
            value: 1
          })
          dispatch(setPaymentStatus(PaymentStatusValues.Success))
          dispatch(setBookingStatus(BookingStatusValues.Processing))
        },
        onPaymentFailure: (error?: string) => {
          logger.warn({message: 'Payment failed, stop polling for status'})
          pushDataLayerEvent({
            event: 'Checkout_Payment_Failure',
            category: 'IBE',
            value: 1
          })
          dispatch(setPaymentStatus(PaymentStatusValues.Failure))
          dispatch(setPaymentError(error))
          dispatch(setBookingStatus(BookingStatusValues.Inactive))
        }
      })
    }
  }, [paymentSessionId])

  // poll for booking confirmation
  useEffect(() => {
    if (bookingStatus === BookingStatusValues.Processing && paymentSessionId) {
      // poll for booking confirmation
      logger.info({
        message: 'Payment succeeded, start polling for booking confirmation',
        data: {paymentSessionId}
      })

      pollForBookingConfirmation({
        paymentSessionId,
        onBookingSuccess: (bookingId: string) => {
          logger.info({
            message: 'Booking processed successfully',
            data: {bookingId}
          })

          // Calculate total revenue from cart
          const cartTotals = calcCartPriceSummary(cartItems)
          const reservationId = `${bookingId}-1`

          pushDataLayerEvent({
            event: 'Checkout_Booking_Success',
            category: 'IBE',
            value: 1,
            totalRevenue: cartTotals.total,
            reservationId
          })
          dispatch(setBookingId(bookingId))
          dispatch(setBookingStatus(BookingStatusValues.Success))
          dispatch(setAppView(AppView.CONFIRMATION))
        },
        onBookingFailure: () => {
          logger.warn({
            message: 'Booking failed, stop polling for confirmation'
          })
          dispatch(setBookingStatus(BookingStatusValues.Failure))
        }
      })
    }
  }, [bookingStatus, paymentSessionId])

  return (
    <>
      {isOpen && (
        <FocusTrap active={isOpen}>
          <div className="modal modal-open p-4" role="dialog" aria-modal="true">
            <div className="modal-box">
              <div className="modal-body w-full p-6">
                {paymentStatus === PaymentStatusValues.Processing && (
                  <div className="flex flex-col items-center justify-center gap-2 p-4">
                    <img
                      src={IbeProgress}
                      alt="Processing your payment image."
                      className="max-h-48"
                    />
                    <div
                      className="flex flex-row items-center gap-2 mt-2"
                      tabIndex={0}
                    >
                      <span className="loading loading-spinner loading-sm"></span>
                      <span className="text-lg font-semibold">
                        Processing your payment...
                      </span>
                    </div>
                    <p className="text-center text-sm mx-4">
                      Hang tight - we're processing your payment and will update
                      you shortly. This may take a few minutes. Please do not
                      close this browser window.
                    </p>
                  </div>
                )}

                {paymentStatus === PaymentStatusValues.Failure && (
                  <div className="flex flex-col items-center justify-center gap-2 p-4">
                    <img
                      src={BookingFailed}
                      alt="Booking failed image."
                      className="h-48"
                    />
                    <h2 className="text-lg font-semibold">Payment Failed</h2>
                    <p className="text-center text-sm mx-4">
                      The booking could not be processed because your payment
                      failed. {formatErrorMessage(paymentError)}
                      If problems persist, please contact us.
                    </p>
                    <div>
                      <button
                        className="btn btn-primary"
                        onClick={() => {
                          logger.info({
                            message:
                              'User is trying again to submit a payment...'
                          })
                          pushDataLayerEvent({
                            event: 'Checkout_Payment_Retry',
                            category: 'IBE',
                            value: 1
                          })
                          dispatch(clearPayment())
                          dispatch(clearBooking())
                        }}
                        aria-label="Try again to submit a payment."
                        tabIndex={0}
                      >
                        Try Again
                      </button>
                    </div>
                  </div>
                )}

                {bookingStatus === BookingStatusValues.Processing && (
                  <div className="flex flex-col items-center justify-center gap-2 p-4">
                    <img
                      src={IbeProgress}
                      alt="Processing your booking image."
                      className="max-h-48"
                    />
                    <div
                      className="flex flex-row items-center gap-2 mt-2"
                      tabIndex={0}
                    >
                      <span className="loading loading-spinner loading-sm"></span>
                      <span className="text-lg font-semibold">
                        Processing your booking...
                      </span>
                    </div>
                    <p className="text-center text-sm mx-4">
                      Hang tight - we're processing your booking and will update
                      you shortly. This may take a few minutes. Please do not
                      close this browser window.
                    </p>
                  </div>
                )}

                {bookingStatus === BookingStatusValues.Failure && (
                  <div className="flex flex-col items-center justify-center gap-2 p-4">
                    <img
                      src={BookingFailed}
                      alt="Booking failed image."
                      className="h-48"
                    />
                    {hasSameDayBooking ? (
                      <>
                        <h2 className="text-lg font-semibold">
                          Oops! That didn't go as planned.
                        </h2>
                        <p className="text-center text-sm mx-4">
                          It looks like the rate you selected is no longer
                          available—or the room type may have just sold out.
                          Either way, we're here to help. If you're still trying
                          to book for tonight, the fastest way to lock in your
                          stay is to contact the property directly. Our team
                          will do everything we can to make it work. Please
                          contact us at{' '}
                          {propertyPhone ? (
                            <a
                              className="text-accent"
                              href={`tel:+1-${propertyPhone}`}
                            >
                              {propertyPhone}
                            </a>
                          ) : (
                            <a
                              className="text-accent"
                              href="https://www.placemakr.com/contact-us"
                              target="_blank"
                            >
                              https://www.placemakr.com/contact-us
                            </a>
                          )}{' '}
                          Because even last-minute plans deserve a smarter way
                          to stay.
                        </p>
                      </>
                    ) : (
                      <>
                        <h2 className="text-lg font-semibold">
                          Booking Failed
                        </h2>
                        <p className="text-center text-sm mx-4">
                          There was an issue with your booking. Your payment
                          will be refunded.
                        </p>
                        <button
                          className="btn btn-primary mt-2"
                          onClick={() => {
                            logger.info({
                              message: 'User is trying again to book...'
                            })
                            pushDataLayerEvent({
                              event: 'Checkout_Booking_Retry',
                              category: 'IBE',
                              value: 1
                            })
                            dispatch(clearPayment())
                            dispatch(clearBooking())
                            validate()
                          }}
                          aria-label="Try again to book."
                          tabIndex={0}
                        >
                          Try Again
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </FocusTrap>
      )}
    </>
  )
}

export default CheckoutProcessingModal
